import { useLayoutEffect, useRef } from "react";

/** A waiting worker is already downloaded. Switch only at a quiet, durable boundary. */
export function useAutomaticUpdate(blocked: boolean, boundary: string, prepare: () => Promise<void>) {
  const prepareRef = useRef(prepare);
  prepareRef.current = prepare;
  useLayoutEffect(() => {
    const handle = window.imprintServiceWorkerUpdate;
    if (!handle) return;
    let timer = 0;
    let generation = 0;
    let stopped = false;
    const pause = () => {
      generation++;
      window.clearTimeout(timer);
      handle.setSafeToReload(false);
    };
    const schedule = () => {
      pause();
      if (blocked || !handle.pending || document.visibilityState !== "visible") return;
      const attempt = generation;
      timer = window.setTimeout(async () => {
        // Focused inputs include an unchanged search field and browser composition.
        if (document.activeElement?.matches("input, textarea, select, [contenteditable=true]")) return;
        try {
          await prepareRef.current();
          if (stopped || generation !== attempt || document.visibilityState !== "visible") return;
          handle.setSafeToReload(true);
          await handle.activate();
        } catch {
          // Storage/activation failures leave the working app intact. Retry at the
          // next foreground/interaction boundary, never in a reload loop.
          handle.setSafeToReload(false);
        }
      }, 1200);
    };
    const events = ["pointerdown", "keydown", "input", "scroll", "focusin", "focusout", "pageshow", "online"];
    events.forEach((event) => window.addEventListener(event, schedule, { capture: true, passive: true }));
    document.addEventListener("visibilitychange", schedule);
    const unsubscribe = handle.subscribe(schedule);
    schedule();
    return () => {
      stopped = true;
      pause();
      unsubscribe();
      events.forEach((event) => window.removeEventListener(event, schedule, true));
      document.removeEventListener("visibilitychange", schedule);
    };
  }, [blocked, boundary]);
}
